import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, cartTotal, clearCart } = useCart();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    phone: '',
  });
  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const finalTotal = appliedPromo ? appliedPromo.new_total : cartTotal;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);

    try {
      const response = await axios.post(`${API}/promo-codes/validate`, {
        code: promoCode,
        cart_total: cartTotal
      });
      setAppliedPromo(response.data);
      toast.success(`Promo code applied! $${response.data.discount_amount.toFixed(2)} discount`);
    } catch (error) {
      console.error('Promo code error:', error);
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      setAppliedPromo(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create order
      const orderData = {
        items: cart.map((item) => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
        total: finalTotal,
        customer_name: formData.name,
        customer_email: formData.email,
        customer_address: formData.address,
      };

      const orderResponse = await axios.post(`${API}/orders`, orderData);
      const orderId = orderResponse.data.id;

      // Record promo code usage if applied
      if (appliedPromo) {
        try {
          await axios.post(`${API}/promo-codes/${appliedPromo.code}/use`);
        } catch (error) {
          console.error('Error recording promo code usage:', error);
        }
      }

      toast.success('Order created! Check your email for payment instructions.');
      clearCart();
      navigate(`/order-success?orderId=${orderId}&payment=pending`);
    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="py-24 lg:py-32">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <h1 className="text-4xl sm:text-5xl font-heading font-bold tracking-tighter text-primary-main mb-12" data-testid="checkout-heading">
          Checkout
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Customer Information */}
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <h2 className="text-2xl font-heading font-semibold text-primary-main mb-6">Customer Information</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="name">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  data-testid="checkout-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="email">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  data-testid="checkout-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="phone">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                  data-testid="checkout-phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2" htmlFor="address">
                  Shipping Address *
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows={3}
                  className="w-full border border-slate-200 rounded-md px-4 py-3 focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                  data-testid="checkout-address-input"
                />
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="bg-background-subtle border border-slate-200 rounded-lg p-8">
            <h2 className="text-2xl font-heading font-semibold text-primary-main mb-6">Order Summary</h2>
            <div className="space-y-3 mb-6">
              {cart.map((item) => (
                <div key={item.id} className="flex justify-between text-text-secondary">
                  <span>
                    {item.name} x {item.quantity}
                  </span>
                  <span className="font-mono">${(item.price * item.quantity).toFixed(2)} AUD</span>
                </div>
              ))}
              
              {/* Promo Code Section */}
              <div className="border-t border-slate-200 pt-4">
                {!appliedPromo ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                      placeholder="Enter promo code"
                      className="flex-1 border border-slate-200 rounded-md px-4 py-2 text-sm focus:ring-1 focus:ring-primary-main focus:border-primary-main outline-none"
                      data-testid="promo-code-input"
                    />
                    <button
                      onClick={handleApplyPromo}
                      disabled={promoLoading || !promoCode.trim()}
                      className="px-4 py-2 bg-primary-main text-white rounded-md text-sm font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="apply-promo-button"
                    >
                      {promoLoading ? 'Checking...' : 'Apply'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-success/10 border border-success rounded-md p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-success">Promo code applied: {appliedPromo.code}</p>
                      <p className="text-xs text-text-secondary">
                        {appliedPromo.discount_type === 'percentage' 
                          ? `${appliedPromo.discount_value}% off` 
                          : `$${appliedPromo.discount_value} off`}
                      </p>
                    </div>
                    <button
                      onClick={handleRemovePromo}
                      className="text-xs text-error hover:text-red-700 font-medium"
                      data-testid="remove-promo-button"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Totals */}
              {appliedPromo && (
                <>
                  <div className="flex justify-between text-text-secondary">
                    <span>Subtotal</span>
                    <span className="font-mono">${cartTotal.toFixed(2)} AUD</span>
                  </div>
                  <div className="flex justify-between text-success">
                    <span>Discount</span>
                    <span className="font-mono">-${appliedPromo.discount_amount.toFixed(2)} AUD</span>
                  </div>
                </>
              )}
              
              <div className="border-t border-slate-200 pt-3 flex justify-between text-lg font-semibold">
                <span className="font-heading">Total</span>
                <span className="font-mono text-primary-main" data-testid="checkout-total">
                  ${finalTotal.toFixed(2)} AUD
                </span>
              </div>
            </div>
          </div>

          {/* Payment Instructions */}
          <div className="bg-white border border-slate-200 rounded-lg p-8">
            <h2 className="text-2xl font-heading font-semibold text-primary-main mb-6">Payment Instructions</h2>
            <div className="bg-accent-subtle border border-primary-main/30 rounded-lg p-6 mb-6">
              <p className="text-sm text-text-secondary mb-4">
                <strong>Bank Transfer Payment:</strong> After placing your order, you'll receive payment instructions via email.
              </p>
              <p className="text-sm text-text-secondary">
                Once we confirm your payment, your order will be processed and shipped within 1-2 business days.
              </p>
            </div>
            <div className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <div className="bg-primary-main text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                <div>
                  <p className="font-medium text-text-primary">Place your order</p>
                  <p className="text-text-secondary">Complete the form and click "Place Order"</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary-main text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                <div>
                  <p className="font-medium text-text-primary">Receive payment details</p>
                  <p className="text-text-secondary">We'll email you bank account details and your order number</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary-main text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">3</div>
                <div>
                  <p className="font-medium text-text-primary">Transfer payment</p>
                  <p className="text-text-secondary">Use your bank app to transfer the exact amount</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="bg-primary-main text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-xs font-bold">4</div>
                <div>
                  <p className="font-medium text-text-primary">Order ships</p>
                  <p className="text-text-secondary">We'll confirm payment and ship your order with tracking</p>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-6">
            <p className="text-xs text-text-secondary">
              <strong className="text-text-primary">IMPORTANT:</strong> By placing this order, you confirm that all products are intended for laboratory research purposes only. Not for human consumption or veterinary use.
            </p>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/cart')}
              className="btn-secondary flex-1"
              data-testid="back-to-cart-button"
            >
              Back to Cart
            </button>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex-1"
              data-testid="place-order-button"
            >
              {loading ? 'Processing...' : 'Place Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
